import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Wallet, Users, Briefcase, type LucideIcon } from 'lucide-react';
import KPICard, { type KPIMetric } from './KpiCard';
import type { DashboardScope } from '../DashboardContext';

export type KPISectionProps = {
  primaryKPIs: KPIMetric[];
  secondaryKPIs: {
    attendance: KPIMetric[];
    payroll: KPIMetric[];
    employees: KPIMetric[];
    projects: KPIMetric[];
  };
  scope?: DashboardScope;
};

type GroupKey = 'attendance' | 'payroll' | 'employees' | 'projects';

const groupMeta: Record<GroupKey, { icon: LucideIcon; labelKey: string; cols: string }> = {
  attendance: { icon: Clock, labelKey: 'Attendance', cols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' },
  payroll: { icon: Wallet, labelKey: 'Payroll', cols: 'grid-cols-1 md:grid-cols-2' },
  employees: { icon: Users, labelKey: 'Employees', cols: 'grid-cols-1 md:grid-cols-2' },
  projects: { icon: Briefcase, labelKey: 'Projects', cols: 'grid-cols-1 md:grid-cols-2' },
};

const KPISection: React.FC<KPISectionProps> = ({ primaryKPIs, secondaryKPIs }) => {
  const { t } = useTranslation();

  const renderGroup = (key: GroupKey, items: KPIMetric[]) => {
    if (!items?.length) return null;
    const { icon: GroupIcon, labelKey, cols } = groupMeta[key];
    return (
      <div data-testid={`kpi-group-${key}`}>
        <div className="flex items-center gap-2 mb-3 ps-1">
          <GroupIcon className="w-3.5 h-3.5 text-muted-foreground/70" strokeWidth={2.25} />
          <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">
            {t(labelKey)}
          </h3>
          <div className="flex-1 h-px bg-border/60" />
        </div>
        <div className={`grid gap-4 ${cols}`}>
          {items.map((metric) => (
            <KPICard key={metric?.id} metric={metric} isPrimary={false} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-7" data-testid="kpi-section">
      {/* Primary KPIs - Tier 1 */}
      {primaryKPIs?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="primary-kpis">
          {primaryKPIs.map((metric) => (
            <KPICard key={metric?.id} metric={metric} isPrimary={true} />
          ))}
        </div>
      )}

      {/* Secondary KPIs - Tier 2 (Grouped) */}
      <div className="space-y-6" data-testid="secondary-kpis">
        {renderGroup('attendance', secondaryKPIs?.attendance)}
        {renderGroup('payroll', secondaryKPIs?.payroll)}
        {renderGroup('employees', secondaryKPIs?.employees)}
        {renderGroup('projects', secondaryKPIs?.projects)}
      </div>
    </div>
  );
};

export default KPISection;
